import {
  getMultiMarketDataSnapshotRequestKey,
  type SecurityMarketDataColumnParams,
  type UseMultiMarketDataSnapshotRequest,
} from '@talos/kyoko';
import type { Column } from 'ag-grid-community';
import { getMultilegLegMarketAccounts } from '../hooks/actions/common/getMultilegLegMarketDetails';
import type { PriceGridEntity } from '../PriceGridEntity';
import { PRICE_GRID_MARKET_DATA_COLUMN_TYPE } from './constants';
import { getLegIndexFromPriceGridMarketDataColumnField } from './priceGridMarketDataColumnUtils';
import type { PriceGridMarketDataRequestsParams } from './types';
import { getPriceGridCellSymbol, getPriceGridColumnParams } from './utils';

function getSecurityMarketDataColumnParams(column: Column): SecurityMarketDataColumnParams | undefined {
  return getPriceGridColumnParams<SecurityMarketDataColumnParams>(column, PRICE_GRID_MARKET_DATA_COLUMN_TYPE);
}

function getColumnMarkets(columnParams: SecurityMarketDataColumnParams | undefined): string[] | undefined {
  const markets = columnParams?.Markets;
  return Array.isArray(markets) ? markets : undefined;
}

function getMarketFromMarketAccountName(selection: string) {
  return selection.includes('/') ? selection.split('/')[0] : undefined;
}

export function getPriceGridRequestMarkets({
  selectedMarkets,
  securityMarkets,
}: {
  selectedMarkets: string[] | undefined;
  securityMarkets: string[] | undefined;
}): string[] | undefined {
  if (!selectedMarkets) {
    return undefined;
  }

  if (selectedMarkets.length === 0) {
    return [];
  }

  const securityMarketsSet = new Set(securityMarkets ?? []);
  const resolvedMarkets = selectedMarkets.filter(selection => {
    if (securityMarketsSet.has(selection)) {
      return true;
    }

    const market = getMarketFromMarketAccountName(selection);
    return market != null && securityMarketsSet.has(market);
  });

  return resolvedMarkets.length > 0 ? resolvedMarkets : undefined;
}

function getPriceGridLegRequestMarkets({
  selectedMarkets,
  dataSecurity,
  column,
}: {
  selectedMarkets: string[] | undefined;
  dataSecurity: PriceGridEntity['security'];
  column: Column;
}): string[] | undefined {
  if (selectedMarkets && selectedMarkets.length > 0) {
    return selectedMarkets;
  }

  const legIndex = getLegIndexFromPriceGridMarketDataColumnField(column.getColDef().field);
  const legMarketAccounts = getMultilegLegMarketAccounts({ dataSecurity, legIndex });

  return legMarketAccounts.length > 0 ? legMarketAccounts : selectedMarkets;
}

export function getPriceGridMarketDataRequest(
  column: Column,
  item: PriceGridEntity
): UseMultiMarketDataSnapshotRequest | undefined {
  const symbol = getPriceGridCellSymbol(column, item);
  if (!symbol) {
    return undefined;
  }

  const columnParams = getSecurityMarketDataColumnParams(column);
  if (!columnParams) {
    return undefined;
  }

  const selectedMarkets = getColumnMarkets(columnParams);
  const isParentSecurityRequest = symbol === item.security.Symbol;
  const markets = isParentSecurityRequest
    ? getPriceGridRequestMarkets({
        selectedMarkets,
        securityMarkets: item.security.Markets,
      })
    : getPriceGridLegRequestMarkets({ selectedMarkets, dataSecurity: item.security, column });
  if (isParentSecurityRequest && selectedMarkets && selectedMarkets.length > 0 && !markets) {
    return undefined;
  }

  const requestParams = columnParams.getRequestParams?.({
    data: item,
    symbol,
  });
  const sizeBuckets = requestParams?.SizeBuckets;
  const depth = requestParams?.Depth;
  const priceIncrement = requestParams?.PriceIncrement;
  const key = getMultiMarketDataSnapshotRequestKey({
    Symbol: symbol,
    Markets: markets,
    SizeBuckets: sizeBuckets,
    Depth: depth,
    PriceIncrement: priceIncrement,
  });
  return {
    key,
    Symbol: symbol,
    Markets: markets,
    SizeBuckets: sizeBuckets,
    Depth: depth,
    PriceIncrement: priceIncrement,
  };
}

export function buildPriceGridMarketDataRequests({
  visibleColumns,
  renderedItems,
}: PriceGridMarketDataRequestsParams): UseMultiMarketDataSnapshotRequest[] {
  const requestsByKey = new Map<string, UseMultiMarketDataSnapshotRequest>();

  for (const column of visibleColumns) {
    for (const item of renderedItems) {
      const requestDetails = getPriceGridMarketDataRequest(column, item);
      if (requestDetails) {
        requestsByKey.set(requestDetails.key, requestDetails);
      }
    }
  }

  return [...requestsByKey.values()];
}
